import { ReactNode } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

type SortableDescriptorListPropsType = {
    ids: string[];
    onMove: (id: string, newIndex: number) => void;
    children: ReactNode;
};

/**
 * Drag-and-drop reordering context for a single nesting level of descriptors.
 * Each level gets its own context, so items can only be reordered among
 * their siblings and never dragged into another parent.
 */
function SortableDescriptorList({ ids, onMove, children }: SortableDescriptorListPropsType) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        const newIndex = ids.indexOf(String(over.id));
        if (newIndex >= 0) {
            onMove(String(active.id), newIndex);
        }
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                {children}
            </SortableContext>
        </DndContext>
    );
}

export default SortableDescriptorList;
